import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export type NavDirection = 'prev' | 'next'

export interface NavButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Direction of navigation (determines arrow direction) */
  direction: NavDirection
  /** Whether the button is disabled */
  disabled?: boolean
  /** Called when button is clicked */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled NavButton component.
 *
 * An atomic navigation button with directional arrow icon.
 * All visual state determined by props.
 */
const NavButton = React.memo(
  ({
    direction,
    disabled = false,
    onClick,
    className,
    style,
    ...props
  }: NavButtonProps) => {
    const [css, theme] = useStyletron()

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      if (!disabled) {
        onClick?.(e)
      }
    }

    const buttonClassName = css(getButtonStyles(theme))

    const ariaLabel = direction === 'prev' ? 'Previous page' : 'Next page'

    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={`${buttonClassName} ${className || ''}`}
        style={style}
        aria-label={ariaLabel}
        {...props}
      >
        {direction === 'prev' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
      </button>
    )
  }
)

NavButton.displayName = 'NavButton'

// ═══════════════════════════════════════════════════════════════════════════
// Icons
// ═══════════════════════════════════════════════════════════════════════════

const ChevronLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
)

const ChevronRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

const getButtonStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: theme.colors.grey70,
  borderRadius: '4px',
  transition: 'background-color 150ms ease, color 150ms ease',
  ':hover:not(:disabled)': {
    backgroundColor: theme.colors.grey10,
    color: theme.colors.grey90,
  },
  ':disabled': {
    cursor: 'default',
    color: theme.colors.grey50,
  },
})

export default NavButton
